/**
 * Base-view simulation — pure TypeScript, no Three.js, no DOM.
 *
 * Owns player movement (with acceleration/deceleration and smoothed facing),
 * collision against world objects (multi-pass, substepped to avoid tunneling
 * at high dt), proximity detection for interactables, and the dialog finite
 * state machine. Deterministic given (dt, input, cameraYaw), which makes it
 * unit-testable in Node/jsdom.
 */
import type {
  BaseSim,
  DialogNode,
  InputState,
  NearestInteractable,
  NpcActivity,
  ObjectId,
  PlayerState,
  RoutineStep,
  SimSnapshot,
  Vec2,
  WorldData,
  WorldObject,
} from '../types';

export interface SimOptions {
  /** Player spawn on the ground plane. */
  spawn?: Vec2;
  /** How close (metres, edge-to-centre) counts as "can talk". */
  talkRange?: number;
  /** Acceleration toward top speed while movement input is held (metres/second^2). */
  acceleration?: number;
  /** Deceleration toward a stop once movement input is released (metres/second^2). */
  deceleration?: number;
  /** Turn rate toward the movement-facing direction (radians/second). */
  turnSpeed?: number;
  /**
   * Target speed (metres/second) used instead of `PlayerState.speed` while
   * `input.gait === 'walk'`. Deliberately a separate field, not a
   * multiplier applied in place to `player.speed` — `PlayerState.speed` is
   * documented as "Max ground speed" and `base-sim.movement.test.ts` reads
   * it as the expected-distance basis (`speed * dt`); scaling it in place
   * would silently corrupt those tests instead of failing loudly. The
   * default is an eyeballed placeholder, same as `player.speed`'s own
   * hardcoded `7` — neither is wired to the server-computed
   * `equipment.blendedStats.walkSpeed`/`runSpeed` already shown in the
   * paperdoll (that needs sim recreation or a setter on gear change, real
   * architecture outside this pass — see character-animation-runtime.md §3.7).
   */
  walkSpeed?: number;
}

/** Below this speed the player is considered stopped (m/s). */
const MOVING_EPS = 0.05;
/** Eyeballed placeholder walk-gait speed (m/s) — see SimOptions.walkSpeed's doc comment. */
const DEFAULT_WALK_SPEED = 3.5;
/**
 * World-space run speed (m/s) at GDD stat multiplier 1.0 — the fixed anchor
 * `setStatSpeeds()`'s `runSpeedMultiplier`/`crouchSpeedMultiplier` args scale
 * against. This WAS the sim's inline `player.speed` default (bare `7`); the
 * only thing that changed by naming it is that "how fast is GDD-multiplier
 * 1.0 in world units" is now one visible knob instead of an unnamed literal
 * duplicated wherever it's needed. Character-stats.md F2's `runSpeed` is a
 * multiplier, not a literal m/s value — base exactly 1.0, and the full AGI
 * 1-9 spread only moves it ±8% (`agiRunBonus`, character-stats.md:111) —
 * too small a range to be a standalone velocity; reading it as "1.0 = the
 * default run speed, scaled by stats/gear from there" is what makes
 * `walkSpeed(0.7 GDD) < runSpeed(~1.0-1.08 GDD)` stay a walk-slower-than-run
 * relationship once bridged (character-animation-runtime.md §3.4/§3.7's
 * "bridge live runSpeed into the sim" follow-up). `DEFAULT_WALK_SPEED`
 * itself is NOT derived from this the same way (0.7 * 7 = 4.9, not the 3.5
 * above) — it was eyeballed independently and stays untouched by this pass
 * per explicit instruction; flagging the mismatch here rather than silently
 * "fixing" a value nobody asked to change.
 */
const DEFAULT_RUN_SPEED = 7;
/**
 * TODO(balance-check): un-tuned placeholder — `crouchBaseSpeed` and both
 * coefficients below are eyeballed guesses (no design pass has happened),
 * same status as `character.config.ts`'s own `TODO(balance-check)` fields.
 * `crouchBaseSpeed = 0.45` is chosen so `crouchSpeedMultiplier()`'s result
 * stays below `walkSpeed`'s GDD value (0.7, character-stats.md:110) even at
 * ATTR_MAX (AGI=PER=9) — crouching should read as more careful/slower than
 * a walk at every attribute spread, not just the low end.
 */
const CROUCH_BASE_SPEED = 0.45;
/** TODO(balance-check) — see CROUCH_BASE_SPEED's doc comment. */
const CROUCH_AGI_COEF = 0.02;
/**
 * TODO(balance-check) — see CROUCH_BASE_SPEED's doc comment. "Stealth" has
 * no modeled attribute or derived stat anywhere in character-stats.md (the
 * five are STR/INT/STA/AGI/PER) — Perception stands in as the closest
 * existing concept until a real stat is designed (character-animation-
 * runtime.md §3.4/§5's Open Question 7; Raid's interaction table mentions
 * "stealth" once informally but never formalises it). Equal to
 * CROUCH_AGI_COEF: there is no design basis yet to weight one stat over the
 * other, so this doesn't pretend to know one.
 */
const CROUCH_STEALTH_COEF = 0.02;
/**
 * GDD-space `crouchSpeedMultiplier`'s value before the live AGI/PER bridge
 * (`setStatSpeeds`) first connects — the formula's own "zero bonus" value
 * (`CROUCH_BASE_SPEED` alone, matching how `runSpeedMultiplier`'s own
 * pre-bridge default of `1` is `runSpeed`'s zero-bonus value).
 */
const DEFAULT_CROUCH_SPEED_MULTIPLIER = CROUCH_BASE_SPEED;

/**
 * F2 (new, client-only) — `crouchSpeed`, GDD-space (character-stats.md F2;
 * same "1.0 = base" unit system as `runSpeed` — see `DEFAULT_RUN_SPEED`'s
 * doc comment for how a GDD-space multiplier maps to world-space m/s).
 * Multiplicative per the user's verbatim spec ("Crouch speed has a base but
 * is multiplied with character stats — stealth and agility"), matching the
 * exact formula shape character-animation-runtime.md §3.4 settled on:
 * `crouchBaseSpeed × (1 + AGI * crouchAgiCoef + PER * crouchStealthCoef)`.
 *
 * Client-only by design (no `CharacterConfigValues` field, no backend
 * change) — Base View movement is not server-validated per-frame, so this
 * has no anti-cheat/authority implication, and the task this was built
 * against explicitly scoped it to compute from attributes already exposed
 * client-side (`character.attributes` — see game-state.types.ts) rather
 * than add a round-trip for one derived movement-feel number. This is a
 * deliberate, flagged deviation from the project's general "no hardcoded
 * tuning values" rule, not an oversight.
 *
 * Documented at design/gdd/character-stats.md F2 — keep the two in sync by
 * hand (no shared-types package between frontend/backend, same convention
 * game-state.types.ts already follows for the rest of this sheet).
 */
export function crouchSpeedMultiplier(agility: number, perception: number): number {
  return CROUCH_BASE_SPEED * (1 + agility * CROUCH_AGI_COEF + perception * CROUCH_STEALTH_COEF);
}

/**
 * Ambient citizen stroll speed (m/s) — see `WorldObject.wanderRadius`.
 * Deliberately well under `DEFAULT_WALK_SPEED` (3.5, itself an eyeballed
 * placeholder that reads as a jog): a citizen crossing the 64m settlement at
 * player pace looks frantic. The tradeoff is mild foot-skate, since
 * `walk-legs` was authored against the player's cadence, not this one —
 * acceptable at settlement viewing distance for a background body, and fixable
 * later by time-scaling the clip rather than by speeding the citizens up.
 */
const WANDER_SPEED = 1.6;
/** Within this of its target (m), a wanderer picks the next one. */
const WANDER_ARRIVE_EPS = 0.35;
/**
 * Within this of a routine step's position (m), the NPC counts as arrived.
 * Tighter than the wanderers' 0.35: a routine stop is an authored mark (a
 * seat, a workbench, a firing point) and stopping a third of a metre short of
 * a chair reads as missing it.
 */
const ROUTINE_ARRIVE_EPS = 0.15;
/**
 * Random draws allowed when picking a target clear of buildings before giving
 * up and standing at the anchor for that leg. A citizen anchored in a tight
 * spot would otherwise spin the loop forever.
 */
const WANDER_TARGET_TRIES = 12;
/** Clearance (m) a wanderer keeps from a solid footprint when picking a target. */
const WANDER_CLEARANCE = 0.6;

/** Collision resolution passes per substep — settles multi-object overlap. */
const COLLISION_ITERATIONS = 3;
/** Hard cap on substeps per frame so pathological dt cannot spin the loop. */
const MAX_SUBSTEPS = 32;
const TWO_PI = Math.PI * 2;
const EPS = 1e-8;

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));

/**
 * Seeded PRNG (mulberry32) for wander targets, seeded per citizen from its
 * object id. `Math.random()` here would make citizen movement untestable and
 * differ between reloads for no gain — the same reasoning
 * `character-animations.ts`'s `phaseFromId` already applies to idle cycling.
 */
function seededRandom(seed: string): () => number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h = Math.imul(h ^ seed.charCodeAt(i), 16777619);
  }
  let a = h >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Live wander state for one citizen. `anchor` is the authored `position`, never written back. */
interface WandererState {
  objectId: string;
  anchor: Vec2;
  radius: number;
  position: Vec2;
  facing: number;
  target: Vec2;
  rand: () => number;
}

/**
 * Live state for one scripted-routine NPC.
 *
 * `phase` is the whole state machine: TRAVEL walks toward the current step's
 * position, DWELL sits there running down `timer`. Splitting them matters
 * because a step's `dwell` is time spent AT the stop, not including the walk —
 * otherwise a step's duration would depend on how far the previous stop was,
 * and authoring predictable schedules would be impossible.
 */
interface RoutineState {
  objectId: string;
  anchor: Vec2;
  steps: readonly RoutineStep[];
  speed: number;
  position: Vec2;
  facing: number;
  stepIndex: number;
  phase: 'travel' | 'dwell';
  /** Seconds left of the current dwell. Unused while travelling. */
  timer: number;
}

/**
 * Collision/proximity footprint for an object — `collisionW`/`collisionD`
 * when set, else `size.w`/`size.d`. Separate from `size` because a real GLB's
 * visual bounds (decorative overhangs, rubble, steps) rarely match the actual
 * walkable-blocking core of the structure; letting the two diverge means a
 * player can stand closer to a building than its full model extent without
 * making the whole settlement's collision boxes bigger than they look.
 */
function collisionSize(obj: WorldObject): { w: number; d: number } {
  return { w: obj.collisionW ?? obj.size.w, d: obj.collisionD ?? obj.size.d };
}

/**
 * Circle-vs-OBB push-out: given a query point (typically the player) and a
 * box (centre, half-extents, optional yaw), returns the vector to add to the
 * query point to resolve the overlap, or `null` if they don't overlap.
 *
 * Exported as a pure, side-effect-free function — deliberately NOT a
 * `BaseSimImpl` method — so it can be unit-tested directly with exact,
 * deterministic inputs. Testing it only through `BaseSim.update()` (movement
 * + substeps + multi-pass resolution all entangled) hid a real bug behind a
 * second problem: querying exactly at a rotated box's own centre is a
 * genuinely degenerate case (the "nearest face" is ambiguous, and which way
 * it resolves flips on a sub-millimetre perturbation — such as the tiny
 * pre-collision movement a walking test applies before resolution ever
 * runs), so a test built on that entanglement can flip its own expected
 * result depending on incidental timing, independent of whether the
 * rotation math is actually correct. Testing this function directly, at a
 * deliberately non-degenerate point, removes that confound entirely.
 *
 * Approach: transform the query point into the box's own (unrotated) local
 * frame, do ordinary axis-aligned closest-point math there, then rotate the
 * resulting push-out vector back to world space. At `rotationY = 0` this is
 * numerically identical to plain axis-aligned math.
 *
 * Matches THREE.js's Y-rotation convention exactly (`Object3D.rotation.y`,
 * i.e. `Matrix4.makeRotationY`): world = M·local where
 * `M = [[cos, sin], [-sin, cos]]` (this is also `PlayerState.facing`'s own
 * convention, 0 = +Z, positive angle turns +Z toward +X). World→local is
 * therefore `M⁻¹ = Mᵀ = [[cos, -sin], [sin, cos]]` — the two must be each
 * other's inverse, not the same matrix reused both ways (a real bug caught
 * here: an earlier version applied `M` for world→local and `Mᵀ` for
 * local→world, i.e. backwards both times — equivalent to resolving
 * collision at `-rotationY` instead of `+rotationY`. Invisible at
 * `rotationY` = 0 or a 90°/180° multiple, since those happen to be
 * direction-symmetric for a rectangle, but wrong — mirroring the box's true
 * orientation — at any other angle).
 */
export function boxCirclePushOut(
  queryX: number,
  queryZ: number,
  centerX: number,
  centerZ: number,
  hw: number,
  hd: number,
  radius: number,
  rotationY = 0,
): { dx: number; dz: number } | null {
  const wdx = queryX - centerX;
  const wdz = queryZ - centerZ;
  const cos = Math.cos(rotationY);
  const sin = Math.sin(rotationY);
  // World -> local: Mᵀ = [[cos, -sin], [sin, cos]].
  const lx = wdx * cos - wdz * sin;
  const lz = wdx * sin + wdz * cos;

  // Closest point on the (axis-aligned, in local space) box footprint.
  const clx = clamp(lx, -hw, hw);
  const clz = clamp(lz, -hd, hd);
  const ldx = lx - clx;
  const ldz = lz - clz;
  const d2 = ldx * ldx + ldz * ldz;
  if (d2 >= radius * radius) return null;

  let pushLx: number;
  let pushLz: number;
  if (d2 < EPS) {
    // Local-space centre inside the box: push out along whichever local
    // axis needs the least distance to escape.
    const penX = hw + radius - Math.abs(lx);
    const penZ = hd + radius - Math.abs(lz);
    if (penX < penZ) {
      pushLx = Math.sign(lx || 1) * penX;
      pushLz = 0;
    } else {
      pushLx = 0;
      pushLz = Math.sign(lz || 1) * penZ;
    }
  } else {
    const d = Math.sqrt(d2);
    const push = (radius - d) / d;
    pushLx = ldx * push;
    pushLz = ldz * push;
  }

  // Local -> world: M = [[cos, sin], [-sin, cos]] (THREE.js's own forward Y-rotation matrix).
  return {
    dx: pushLx * cos + pushLz * sin,
    dz: -pushLx * sin + pushLz * cos,
  };
}

/** Wrap an angle (radians) into (-PI, PI]. */
function normalizeAngle(angle: number): number {
  let a = angle % TWO_PI;
  if (a > Math.PI) a -= TWO_PI;
  else if (a <= -Math.PI) a += TWO_PI;
  return a;
}

/** Shortest signed distance from `from` to `to`, in (-PI, PI]. */
function shortestAngleDelta(from: number, to: number): number {
  let delta = (to - from) % TWO_PI;
  if (delta > Math.PI) delta -= TWO_PI;
  else if (delta < -Math.PI) delta += TWO_PI;
  return delta;
}

/** Step `current` toward `target` (shortest way round), at most `maxDelta` radians. */
function turnToward(current: number, target: number, maxDelta: number): number {
  const delta = shortestAngleDelta(current, target);
  if (Math.abs(delta) <= maxDelta) return normalizeAngle(target);
  return normalizeAngle(current + Math.sign(delta) * maxDelta);
}

/** Validate cross-references in the world data; throws a single Error listing every problem found. */
function validateWorld(world: WorldData): void {
  const problems: string[] = [];

  for (const obj of world.objects) {
    // A body cannot both follow a script and drift randomly. Rejecting the
    // pair beats silently preferring one: the author would see an NPC ignore
    // half of what they wrote, with nothing to explain why.
    if (obj.wanderRadius && obj.routine) {
      problems.push(`object "${obj.id}" sets both wanderRadius and routine — they are mutually exclusive`);
    }
    if (obj.routine && obj.routine.steps.length === 0) {
      problems.push(`object "${obj.id}" has a routine with no steps`);
    }
    for (const [i, step] of (obj.routine?.steps ?? []).entries()) {
      // A non-positive dwell on a step with no position would advance the
      // routine every tick and spin through the whole list each frame.
      if (!(step.dwell > 0)) {
        problems.push(`object "${obj.id}" routine step ${i} needs a positive dwell (got ${step.dwell})`);
      }
    }
  }

  for (const obj of world.objects) {
    if (!obj.interactable) continue;
    if (obj.npcId && obj.lootObjectId) {
      problems.push(`object "${obj.id}" is interactable via both npcId and lootObjectId — exactly one is required`);
      continue;
    }
    if (obj.lootObjectId) continue; // opaque to the sim — see WorldObject.lootObjectId's docstring
    if (!obj.npcId) {
      problems.push(`object "${obj.id}" is interactable but has no npcId or lootObjectId`);
      continue;
    }
    if (!world.npcs[obj.npcId]) {
      problems.push(`object "${obj.id}" references unknown npcId "${obj.npcId}"`);
    }
  }

  for (const npc of Object.values(world.npcs)) {
    const tree = world.dialog[npc.id];
    if (!tree) {
      problems.push(`npc "${npc.id}" has no dialog tree in world.dialog`);
      continue;
    }
    if (!tree[npc.dialogRootId]) {
      problems.push(`npc "${npc.id}" dialogRootId "${npc.dialogRootId}" is not a node in its dialog tree`);
    }
    for (const node of Object.values(tree)) {
      for (const opt of node.options) {
        if (opt.to !== null && !tree[opt.to]) {
          problems.push(
            `npc "${npc.id}" dialog node "${node.id}" option "${opt.label}" points to missing node "${opt.to}"`,
          );
        }
      }
    }
  }

  if (problems.length > 0) {
    throw new Error(`Base-view world data is invalid:\n- ${problems.join('\n- ')}`);
  }
}

class BaseSimImpl implements BaseSim {
  readonly world: WorldData;

  private player: PlayerState;
  /** Current velocity (m/s), internal — not part of the public PlayerState contract. */
  private velocity: Vec2 = { x: 0, z: 0 };
  private nearest: NearestInteractable | null = null;
  /**
   * Objects that are authored `interactable` but are inert RIGHT NOW.
   *
   * The map is static content; whether a thing can be used is sometimes a live
   * fact the server owns. The player's house is the first case: it is baked
   * `interactable: true` because it is interactable once the deed is yours, and
   * before that it is an abandoned building.
   *
   * Suppressing it HERE rather than at each reader is what makes it one rule.
   * `recomputeNearest` is the single source of "what is in reach", so the
   * E-key, the click-to-interact path, the "Press E" prompt, the hover cursor
   * and the hover glow all go dark together. Guarding them one by one is the
   * five-call-site drift `ui/panels.ts` was written to end.
   *
   * Never mutates `world` — the map module is a shared singleton imported by
   * the game AND the editor, so writing `interactable` onto it would leak one
   * player's ownership into the next mount.
   */
  private readonly suppressed = new Set<ObjectId>();
  private dialogNpc: string | null = null;
  private dialogNode: string | null = null;
  private readonly talkRange: number;
  private readonly acceleration: number;
  private readonly deceleration: number;
  private readonly turnSpeed: number;
  private readonly walkSpeed: number;
  /**
   * Crouch top speed (m/s), world-space — mirrors `player.speed`'s own
   * shape (a plain world-space number `update()` reads directly) rather
   * than living on `PlayerState`: nothing outside the sim needs to read it
   * back the way tests read `player.speed`/`SimOptions.walkSpeed` as an
   * expected-distance basis (see `crouchSpeedMultiplier()`'s doc comment
   * for the GDD-space formula this is derived from via `setStatSpeeds`).
   */
  private crouchSpeed: number;
  /** Ambient wandering citizens — see `WorldObject.wanderRadius`. Empty for most maps. */
  private readonly wanderers: WandererState[] = [];
  /** Scripted-routine NPCs — see `WorldObject.routine`. Empty for most maps. */
  private readonly routines: RoutineState[] = [];

  constructor(world: WorldData, opts: SimOptions = {}) {
    validateWorld(world);

    this.world = world;
    this.talkRange = opts.talkRange ?? 2.4;
    this.acceleration = opts.acceleration ?? 30;
    this.deceleration = opts.deceleration ?? 40;
    this.turnSpeed = opts.turnSpeed ?? 10;
    this.walkSpeed = opts.walkSpeed ?? DEFAULT_WALK_SPEED;
    this.crouchSpeed = DEFAULT_RUN_SPEED * DEFAULT_CROUCH_SPEED_MULTIPLIER;
    const spawn = opts.spawn ?? world.spawnPoint ?? { x: 0, z: 18 };
    this.player = {
      position: { x: spawn.x, z: spawn.z },
      facing: Math.PI, // look toward -Z (into the plaza)
      radius: 0.6,
      speed: DEFAULT_RUN_SPEED,
      moving: false,
    };

    for (const obj of world.objects) {
      if (obj.kind !== 'npc' || !obj.routine || obj.routine.steps.length === 0) continue;
      const r: RoutineState = {
        objectId: obj.id,
        anchor: { ...obj.position },
        steps: obj.routine.steps,
        speed: obj.routine.speed && obj.routine.speed > 0 ? obj.routine.speed : WANDER_SPEED,
        position: { ...obj.position },
        facing: obj.rotationY ?? 0,
        stepIndex: 0,
        // Start DWELLing on step 0 rather than travelling to it: an authored
        // body already stands where its first stop is, and starting in TRAVEL
        // would make every NPC in the settlement take a step on frame 1.
        phase: 'dwell',
        timer: obj.routine.steps[0]!.dwell,
      };
      // Apply step 0's authored facing immediately, so a body that starts on
      // a facing-pinned stop (a shooter looking downrange) is not briefly
      // turned the wrong way on the first frame.
      if (r.steps[0]!.facing !== undefined) r.facing = r.steps[0]!.facing!;
      this.routines.push(r);
    }

    for (const obj of world.objects) {
      if (obj.kind !== 'npc' || !obj.wanderRadius || obj.wanderRadius <= 0) continue;
      const w: WandererState = {
        objectId: obj.id,
        anchor: { ...obj.position },
        radius: obj.wanderRadius,
        position: { ...obj.position },
        facing: obj.rotationY ?? 0,
        target: { ...obj.position },
        rand: seededRandom(obj.id),
      };
      w.target = this.pickWanderTarget(w);
      this.wanderers.push(w);
    }

    this.recomputeNearest();
  }

  getSnapshot(): SimSnapshot {
    return {
      player: {
        position: { ...this.player.position },
        facing: this.player.facing,
        radius: this.player.radius,
        speed: this.player.speed,
        moving: this.player.moving,
      },
      interaction: this.interaction(),
      nearestInteractable: this.nearest ? { ...this.nearest } : null,
      actors: [
        ...this.wanderers.map((w) => ({
          objectId: w.objectId,
          position: { ...w.position },
          facing: w.facing,
          // A citizen never stops, so it is always mid-stride — this is what
          // lets `walk` stay one steady clip pair with no state machine.
          activity: 'walk' as NpcActivity,
          hidden: false,
        })),
        ...this.routines.map((r) => ({
          objectId: r.objectId,
          position: { ...r.position },
          facing: r.facing,
          activity: r.phase === 'travel' ? ('walk' as NpcActivity) : (r.steps[r.stepIndex]!.activity ?? 'stand'),
          hidden: r.phase === 'dwell' && r.steps[r.stepIndex]!.hidden === true,
        })),
      ],
    };
  }

  setStatSpeeds(runSpeedMultiplier: number, crouchSpeedMultiplier: number): void {
    this.player.speed = DEFAULT_RUN_SPEED * runSpeedMultiplier;
    this.crouchSpeed = DEFAULT_RUN_SPEED * crouchSpeedMultiplier;
  }

  /**
   * Teleport. See `BaseSim.setPlayerPosition` for why this exists at all.
   *
   * The velocity goes with it, and that is the whole reason this is a method
   * rather than a caller writing `snapshot.player.position` (which it cannot
   * — `getSnapshot` copies). A body moved without clearing its momentum keeps
   * drifting in the direction it was already heading, so a teleport onto a
   * spawn point would slide off it over the next few frames.
   *
   * `recomputeNearest` is re-run because "what is in reach" is derived from
   * the player's position and every reader of it (the E key, the prompt, the
   * hover glow) would otherwise answer for where the player used to be.
   */
  setPlayerPosition(position: Vec2): void {
    this.player.position.x = position.x;
    this.player.position.z = position.z;
    this.velocity.x = 0;
    this.velocity.z = 0;
    this.player.moving = false;
    this.recomputeNearest();
  }

  update(dt: number, input: InputState, cameraYaw: number): void {
    // Ambient citizens keep strolling regardless of dialog/player state — see
    // updateWanderers' doc comment for why this sits above the freeze below.
    this.updateWanderers(dt);
    this.updateRoutines(dt);

    // Movement is frozen while a conversation is open; drop any held velocity
    // so resuming after the dialog closes doesn't carry over a stale glide.
    // Returning here BEFORE any facing math (aim included) is what freezes
    // facing during dialog even if `input.aimYaw` is set — deliberate, not
    // incidental (see base-sim.aim.test.ts).
    if (this.dialogNode !== null) {
      this.velocity.x = 0;
      this.velocity.z = 0;
      this.player.moving = false;
      return;
    }

    const aiming = typeof input.aimYaw === 'number';

    // Aim mode: facing locks DIRECTLY to aimYaw every frame — no
    // `turnToward` smoothing ("always looking where the mouse aims") — and
    // this runs whether or not there's movement input, unlike the
    // movement-facing branch below, so a stationary aiming player still
    // visibly tracks the cursor.
    if (aiming) {
      this.player.facing = normalizeAngle(input.aimYaw as number);
    }

    // Movement-direction basis: ALWAYS camera-relative — W = away from the
    // camera, D = screen-right — whether or not the player is aiming. Facing
    // is a separate concern and stays locked to `aimYaw` above, so aiming
    // gives real strafing and backpedalling: the character keeps looking
    // where the cursor points while WASD keeps meaning what it means
    // everywhere else on screen.
    //
    // ─── Why this is not an aim-relative basis any more ──────────────────
    // It used to be. While aiming, W pushed toward `aimYaw` and A/D strafed
    // perpendicular to it, on the argument that "move forward while aiming"
    // should mean "move toward where I am pointing". That argument is wrong
    // at the keyboard, and Janeks reported it on 2026-09-19 as *"when we aim
    // (RMC hold) the running is inverted"*:
    //
    //   the cursor is what picks `aimYaw`, and a player aiming at something
    //   BETWEEN themselves and the camera has an `aimYaw` pointing back at
    //   the camera. W then walks them toward the camera — i.e. down the
    //   screen — while the very same key walked them up the screen a frame
    //   earlier, before RMB went down. Every key reverses as the cursor
    //   crosses the character, and A/D reverse with them.
    //
    // A basis that flips meaning based on where the mouse happens to be is
    // not a basis the hands can learn. Camera-relative never flips, which is
    // what every third-person shooter does and what the report asks for.
    //
    // Consequence, accepted deliberately: a player can now hold a direction
    // that does not match their facing (strafing, walking backwards). There
    // are no strafe or backpedal clips in the catalog — `LOCOMOTION_ARMS_CLIP`
    // has walk/run/crouchMove only — so those play the forward run for now.
    // Wrong-looking, not broken, and authoring them is its own asset pass.
    const fwdX = -Math.sin(cameraYaw);
    const fwdZ = -Math.cos(cameraYaw);
    const rightX = Math.cos(cameraYaw);
    const rightZ = -Math.sin(cameraYaw);

    let dirX = fwdX * input.moveZ + rightX * input.moveX;
    let dirZ = fwdZ * input.moveZ + rightZ * input.moveX;
    const dirLen = Math.hypot(dirX, dirZ);
    const hasInput = dirLen > 1e-4;
    if (hasInput) {
      dirX /= dirLen;
      dirZ /= dirLen;
      if (!aiming) {
        // Facing tracks input direction immediately; the turn itself is
        // smoothed. Skipped while aiming — facing is already locked to
        // aimYaw above, unsmoothed.
        const targetFacing = Math.atan2(dirX, dirZ);
        this.player.facing = turnToward(this.player.facing, targetFacing, this.turnSpeed * dt);
      }
    }

    // Velocity ramps toward top speed on input, decays to zero on release —
    // a function of dt only, so it stays deterministic frame-to-frame.
    // `targetSpeed` swaps in `walkSpeed` for a walk gait, or `crouchSpeed`
    // when crouched; `player.speed` itself is untouched in either case (see
    // SimOptions.walkSpeed's doc comment). Crouched takes priority over
    // gait rather than stacking multiplicatively with it: the task this was
    // built against gave this as the simplest-correct default, and nothing
    // in character-stats.md or character-animation-runtime.md's crouch
    // section suggests "crouch-walking" should exist as a distinct, even
    // slower fourth speed — crouch already implies deliberate, careful
    // movement regardless of the separate (persistent, Capslock) run/walk
    // toggle, so once crouched the gait toggle simply stops being consulted
    // until crouch is released.
    const targetSpeed = input.crouched
      ? this.crouchSpeed
      : (input.gait === 'walk' ? this.walkSpeed : this.player.speed);
    const targetVx = hasInput ? dirX * targetSpeed : 0;
    const targetVz = hasInput ? dirZ * targetSpeed : 0;
    const rate = hasInput ? this.acceleration : this.deceleration;
    const maxDelta = rate * dt;
    const dvx = targetVx - this.velocity.x;
    const dvz = targetVz - this.velocity.z;
    const dvLen = Math.hypot(dvx, dvz);
    if (dvLen <= maxDelta || dvLen < 1e-6) {
      this.velocity.x = targetVx;
      this.velocity.z = targetVz;
    } else {
      this.velocity.x += (dvx / dvLen) * maxDelta;
      this.velocity.z += (dvz / dvLen) * maxDelta;
    }

    const speed = Math.hypot(this.velocity.x, this.velocity.z);
    this.player.moving = speed > MOVING_EPS;

    if (speed > 1e-6) {
      this.integrateAndResolve(this.velocity.x * dt, this.velocity.z * dt);
    }

    this.recomputeNearest();
  }

  tryInteract(): DialogNode | null {
    if (this.dialogNode !== null) return this.currentDialog();
    if (!this.nearest?.npcId) return null; // no NPC nearby — e.g. a Location's loot object, which has no dialog
    const npcId = this.nearest.npcId;
    const npc = this.world.npcs[npcId];
    if (!npc) return null;
    this.dialogNpc = npcId;
    this.dialogNode = npc.dialogRootId;
    return this.currentDialog();
  }

  chooseOption(optionIndex: number): DialogNode | null {
    const node = this.currentDialog();
    if (!node) return null;
    const opt = node.options[optionIndex];
    if (!opt) return node; // ignore out-of-range choices
    if (opt.to === null) {
      this.endDialog();
      return null;
    }
    this.dialogNode = opt.to;
    return this.currentDialog();
  }

  currentDialog(): DialogNode | null {
    if (this.dialogNpc === null || this.dialogNode === null) return null;
    const tree = this.world.dialog[this.dialogNpc];
    return tree?.[this.dialogNode] ?? null;
  }

  endDialog(): void {
    this.dialogNpc = null;
    this.dialogNode = null;
  }

  setInteractableSuppressed(objectId: ObjectId, suppressed: boolean): void {
    if (suppressed) this.suppressed.add(objectId);
    else this.suppressed.delete(objectId);
    // Applied immediately rather than left to the next `update`, so a caller
    // that suppresses an object the player is already standing next to does not
    // get one more frame in which E still works on it.
    this.recomputeNearest();
  }

  // ---- internals ----

  private interaction(): SimSnapshot['interaction'] {
    if (this.dialogNpc !== null && this.dialogNode !== null) {
      return { kind: 'dialog', npcId: this.dialogNpc, nodeId: this.dialogNode };
    }
    if (this.nearest) {
      return {
        kind: 'near', objectId: this.nearest.objectId, npcId: this.nearest.npcId, lootObjectId: this.nearest.lootObjectId,
      };
    }
    return { kind: 'none' };
  }

  /**
   * Where an object actually IS this frame.
   *
   * For everything static that is its authored `position`. For a
   * routine-driven body it is the live one — proximity has to track the NPC,
   * not the spot they started at, or a guard walking his beat would keep his
   * talk trigger parked back at the tower. Ambient citizens are never
   * interactable so they never reach here, but they are included for
   * consistency rather than relying on that staying true.
   */
  private livePosition(obj: WorldObject): Vec2 {
    const r = this.routines.find((x) => x.objectId === obj.id);
    if (r) return r.position;
    const w = this.wanderers.find((x) => x.objectId === obj.id);
    return w ? w.position : obj.position;
  }

  /** True while a routine step has this body hidden ("gone inside"). */
  private isHidden(objectId: string): boolean {
    const r = this.routines.find((x) => x.objectId === objectId);
    return !!r && r.phase === 'dwell' && r.steps[r.stepIndex]!.hidden === true;
  }

  private recomputeNearest(): void {
    let best: NearestInteractable | null = null;
    for (const obj of this.world.objects) {
      if (!obj.interactable || (!obj.npcId && !obj.lootObjectId)) continue;
      // Authored-interactable but inert right now — see `suppressed`.
      if (this.suppressed.has(obj.id)) continue;
      // Someone who has stepped inside cannot be talked to. Without this the
      // player could hold a conversation with an invisible body.
      if (this.isHidden(obj.id)) continue;
      const { w, d } = collisionSize(obj);
      const footprint = obj.round ? w / 2 : Math.max(w, d) / 2;
      const centreDist = this.dist(this.player.position, this.livePosition(obj));
      const edgeDist = centreDist - footprint;
      if (edgeDist <= this.talkRange && (!best || edgeDist < best.distance)) {
        best = {
          objectId: obj.id, npcId: obj.npcId, lootObjectId: obj.lootObjectId, distance: edgeDist,
        };
      }
    }
    this.nearest = best;
  }

  /**
   * Integrate the frame's total displacement (already `velocity * dt`, computed
   * once) as a straight-line move, but apply it in bounded substeps so fast
   * motion can't tunnel through thin objects. Splitting a linear move into N
   * substeps does not change the unobstructed result — it only adds collision
   * checks along the way.
   */
  private integrateAndResolve(dxTotal: number, dzTotal: number): void {
    const totalDist = Math.hypot(dxTotal, dzTotal);
    if (totalDist < 1e-9) return;
    const maxStep = Math.max(this.player.radius * 0.5, 0.05);
    const steps = Math.min(MAX_SUBSTEPS, Math.max(1, Math.ceil(totalDist / maxStep)));
    const stepX = dxTotal / steps;
    const stepZ = dzTotal / steps;
    for (let i = 0; i < steps; i++) {
      this.player.position.x += stepX;
      this.player.position.z += stepZ;
      this.resolveCollisions();
      this.clampToGround();
    }
  }

  /** Multiple passes so resolving one overlap doesn't leave (or re-create) another. */
  private resolveCollisions(): void {
    for (let pass = 0; pass < COLLISION_ITERATIONS; pass++) {
      let anyPush = false;
      for (const obj of this.world.objects) {
        if (obj.noClip) continue;
        const pushed = obj.round ? this.resolveCircle(obj) : this.resolveAabb(obj);
        anyPush = anyPush || pushed;
      }
      // Freestanding invisible collision volumes (no visual, no WorldObject
      // semantics — see types.ts's CollisionZone) — box-shaped, optionally rotated.
      for (const zone of this.world.collisionZones ?? []) {
        // `blocksMovement: false` is the sight-only zone (smoke, a dark
        // doorway) — see types.ts. Undefined still means "stops you", which
        // is every zone authored before the flag existed.
        if (zone.blocksMovement === false) continue;
        const pushed = this.resolveBoxAt(zone.position, zone.size.w / 2, zone.size.d / 2, zone.rotationY ?? 0);
        anyPush = anyPush || pushed;
      }
      if (!anyPush) break; // stable — no further overlap to resolve this frame
    }
  }

  /** Circle-vs-circle push-out for round footprints (radius = collision size.w / 2). */
  private resolveCircle(obj: WorldObject): boolean {
    const p = this.player.position;
    const r = this.player.radius;
    const objR = collisionSize(obj).w / 2;
    const minDist = r + objR;
    const dx = p.x - obj.position.x;
    const dz = p.z - obj.position.z;
    const d2 = dx * dx + dz * dz;
    if (d2 >= minDist * minDist) return false;
    if (d2 < EPS) {
      // Degenerate: centres coincide — push along an arbitrary fixed axis.
      p.x += minDist;
      return true;
    }
    const d = Math.sqrt(d2);
    const push = (minDist - d) / d;
    p.x += dx * push;
    p.z += dz * push;
    return true;
  }

  /**
   * Circle-vs-OBB push-out for box footprints. Rotation-aware: an object's
   * collision now follows its `rotationY` (previously stayed axis-aligned
   * regardless — found to be a real bug once objects were actually rotated
   * through the editor, e.g. Medbay, and the collision box stuck out from
   * where the rotated visual model didn't cover, reading as "an invisible
   * wall").
   */
  private resolveAabb(obj: WorldObject): boolean {
    const cSize = collisionSize(obj);
    return this.resolveBoxAt(obj.position, cSize.w / 2, cSize.d / 2, obj.rotationY ?? 0);
  }

  /**
   * Circle-vs-OBB push-out against a box given a centre, half-extents, and
   * an optional yaw — shared by `resolveAabb` (a `WorldObject`'s footprint)
   * and `collisionZones`. Thin wrapper: the actual math is
   * `boxCirclePushOut`, a pure function exported and unit-tested
   * independently of player movement/substep dynamics (see its own doc
   * comment for why that separation matters).
   */
  private resolveBoxAt(center: Vec2, hw: number, hd: number, rotationY = 0): boolean {
    const p = this.player.position;
    const push = boxCirclePushOut(p.x, p.z, center.x, center.z, hw, hd, this.player.radius, rotationY);
    if (!push) return false;
    p.x += push.dx;
    p.z += push.dz;
    return true;
  }

  /**
   * Walks every citizen toward its current target, picking a fresh one on
   * arrival. Runs BEFORE `update()`'s dialog freeze deliberately — the
   * settlement should keep moving in the background while the player talks to
   * someone, rather than the whole town freezing mid-step.
   */
  private updateWanderers(dt: number): void {
    for (const w of this.wanderers) {
      const dx = w.target.x - w.position.x;
      const dz = w.target.z - w.position.z;
      const dist = Math.hypot(dx, dz);
      if (dist <= WANDER_ARRIVE_EPS) {
        w.target = this.pickWanderTarget(w);
        continue;
      }
      const step = Math.min(WANDER_SPEED * dt, dist);
      w.position.x += (dx / dist) * step;
      w.position.z += (dz / dist) * step;
      // Snapped, not smoothed: a citizen only changes heading on arrival, so
      // there is no per-frame direction jitter for turn-smoothing to hide.
      w.facing = Math.atan2(dx, dz);
    }
  }

  /**
   * Advances every scripted NPC through its routine.
   *
   * Runs beside `updateWanderers`, above `update()`'s dialog freeze, for the
   * same reason: the town should keep living while the player is in a
   * conversation, rather than everyone stopping mid-stride.
   *
   * Deliberately NOT collision-aware. A routine's stops are authored, so the
   * author is responsible for not routing someone through a wall — and a
   * scripted NPC that got shoved off its path by collision would drift out of
   * sync with its own schedule with no way to recover. `blockedForWanderer`
   * exists for the random walker precisely because nobody authored ITS path.
   */
  /**
   * Start the dwell for the routine's CURRENT step.
   *
   * The single place a dwell begins. It used to be set both here and again on
   * arrival, which was provably redundant — a mutation test that deleted the
   * arrival copy could not be distinguished from the original, because the two
   * always wrote the same value. Redundant state that no test can tell apart
   * is a place for the two copies to drift, so there is now exactly one.
   */
  private beginDwell(r: RoutineState): void {
    const step = r.steps[r.stepIndex]!;
    r.phase = 'dwell';
    r.timer = step.dwell;
    if (step.facing !== undefined) r.facing = step.facing;
  }

  private updateRoutines(dt: number): void {
    for (const r of this.routines) {
      const step = r.steps[r.stepIndex]!;
      if (r.phase === 'dwell') {
        r.timer -= dt;
        if (r.timer > 0) continue;
        r.stepIndex = (r.stepIndex + 1) % r.steps.length;
        // A step with no position is an activity change in place — there is
        // nothing to walk to, so it begins dwelling immediately.
        if (r.steps[r.stepIndex]!.position) r.phase = 'travel';
        else this.beginDwell(r);
        continue;
      }

      const target = step.position ?? r.anchor;
      const dx = target.x - r.position.x;
      const dz = target.z - r.position.z;
      const dist = Math.hypot(dx, dz);
      if (dist <= ROUTINE_ARRIVE_EPS) {
        // SNAP to the mark. A routine stop is an authored position — a seat, a
        // workbench, a firing point — so stopping wherever the last frame's
        // step happened to land (up to ROUTINE_ARRIVE_EPS short, and a
        // different amount every run depending on dt) leaves the NPC beside
        // the chair rather than on it. Snapping also makes the routine's
        // positions exactly reproducible, which is what lets a test assert
        // them and an author trust them.
        r.position.x = target.x;
        r.position.z = target.z;
        this.beginDwell(r);
        continue;
      }
      const move = Math.min(r.speed * dt, dist);
      r.position.x += (dx / dist) * move;
      r.position.z += (dz / dist) * move;
      // Heading follows travel direction, same convention as the wanderers.
      r.facing = Math.atan2(dx, dz);
    }
  }

  /**
   * A random point within the citizen's radius of its anchor that isn't
   * inside a solid footprint or off the ground plane. Falls back to the
   * anchor after `WANDER_TARGET_TRIES` failures — for a citizen anchored
   * somewhere too tight to stroll, standing still beats an infinite search.
   */
  private pickWanderTarget(w: WandererState): Vec2 {
    const hx = this.world.ground.width / 2 - WANDER_CLEARANCE;
    const hz = this.world.ground.depth / 2 - WANDER_CLEARANCE;
    for (let i = 0; i < WANDER_TARGET_TRIES; i++) {
      const angle = w.rand() * TWO_PI;
      // sqrt keeps draws uniform over the disc instead of clustering at the centre.
      const r = Math.sqrt(w.rand()) * w.radius;
      const candidate = {
        x: clamp(w.anchor.x + Math.cos(angle) * r, -hx, hx),
        z: clamp(w.anchor.z + Math.sin(angle) * r, -hz, hz),
      };
      if (!this.blockedForWanderer(candidate)) return candidate;
    }
    return { ...w.anchor };
  }

  /**
   * Is this point inside a solid object's footprint? Reuses the same
   * `boxCirclePushOut`/circle test the player's own collision runs, with
   * `WANDER_CLEARANCE` standing in for the body radius, so a citizen targets
   * a spot it could actually stand in. `noClip` objects (grass, other NPC
   * bodies, decorative props) are walk-through for the player and are
   * deliberately ignored here too.
   */
  private blockedForWanderer(p: Vec2): boolean {
    for (const obj of this.world.objects) {
      if (obj.noClip) continue;
      const cSize = collisionSize(obj);
      if (obj.round) {
        const minDist = WANDER_CLEARANCE + cSize.w / 2;
        if (Math.hypot(p.x - obj.position.x, p.z - obj.position.z) < minDist) return true;
        continue;
      }
      const push = boxCirclePushOut(
        p.x, p.z, obj.position.x, obj.position.z,
        cSize.w / 2, cSize.d / 2, WANDER_CLEARANCE, obj.rotationY ?? 0,
      );
      if (push) return true;
    }
    for (const zone of this.world.collisionZones ?? []) {
      if (zone.blocksMovement === false) continue; // walk-through, see resolveCollisions
      const push = boxCirclePushOut(
        p.x, p.z, zone.position.x, zone.position.z,
        zone.size.w / 2, zone.size.d / 2, WANDER_CLEARANCE, zone.rotationY ?? 0,
      );
      if (push) return true;
    }
    return false;
  }

  private clampToGround(): void {
    const r = this.player.radius;
    const hx = this.world.ground.width / 2 - r;
    const hz = this.world.ground.depth / 2 - r;
    this.player.position.x = clamp(this.player.position.x, -hx, hx);
    this.player.position.z = clamp(this.player.position.z, -hz, hz);
  }

  private dist(a: Vec2, b: Vec2): number {
    return Math.hypot(a.x - b.x, a.z - b.z);
  }
}

/** Factory — construct a base-view simulation over the given world. */
export function createBaseSim(world: WorldData, opts?: SimOptions): BaseSim {
  return new BaseSimImpl(world, opts);
}

/** Re-exported for tests that want to assert on object typing. */
export type { WorldObject };
